import React, { useRef, useEffect, useState, useMemo } from 'react';
import { generateLetterOffsets } from '../00-shared/constants';
import { AnimatedText } from '../00-shared/AnimatedText';
import DataFlowDiagram from '../00-shared/DataFlowDiagram';
import WorkflowDiagram from '../00-shared/WorkflowDiagram';
import MockTrendChart from '../00-shared/MockTrendChart';
import SecurityDiagram from '../00-shared/SecurityDiagram';

const ValuePropositionSection: React.FC = () => {
    const [titleAnimated, setTitleAnimated] = useState(false);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const titleText = 'Smarter tools for Coaches. Better Outcomes for Athletes.';
    const subtitleText = 'CGM Sports supports teams and athletes simplify their data and enhance performance.';

    const titleLetterOffsets = useMemo(() => generateLetterOffsets(titleText, 100, 200), []);
    const subtitleLetterOffsets = useMemo(() => generateLetterOffsets(subtitleText, 100, 100), []);

    const processSteps = [
        {
            number: '01',
            title: 'Data Ingestion',
            description: 'Seamlessly connect wearable, testing, and wellness data across your team.',
        },
        {
            number: '02',
            title: 'Workflows',
            description: 'Automate reports and readiness alerts that fit your team\u2019s training schedule.',
        },
        {
            number: '03',
            title: 'Analysis',
            description: 'Turn complex performance data into clear, actionable insights across strength, recovery, and readiness.',
        },
        {
            number: '04',
            title: 'Security',
            description: 'HIPAA & FERPA compliant. Your data stays secure and under your control.',
        },
    ];

    // Visualization component for each step
    const stepVisualizations: Record<string, React.ReactNode> = {
        '01': <DataFlowDiagram className="py-4" />,
        '02': <WorkflowDiagram className="py-4" />,
        '03': (
            <div className="border border-neutral-700 p-4">
                <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-[#3B82F6]" />
                        <span className="text-[10px] text-gray-400">HRV Trend</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-[#059669]" />
                        <span className="text-[10px] text-gray-400">Recovery Score</span>
                    </div>
                </div>
                <MockTrendChart />
            </div>
        ),
        '04': <SecurityDiagram className="py-4" />,
    };

    useEffect(() => {
        if (!titleRef.current) return;

        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px 200px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !titleAnimated) {
                    setTitleAnimated(true);
                }
            });
        }, observerOptions);

        observer.observe(titleRef.current);

        return () => observer.disconnect();
    }, [titleAnimated]);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = parseInt(entry.target.getAttribute('data-index') || '0');
                    const animationClass = index % 2 === 0 ? 'fade-in-left' : 'fade-in-right';
                    entry.target.classList.add(animationClass);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.observe-fade-directional').forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="pb-24 text-center">
                    <AnimatedText
                        as="h2"
                        text={titleText}
                        offsets={titleLetterOffsets}
                        animated={titleAnimated}
                        className="text-3xl md:text-5xl text-white mb-4"
                    />
                    <div ref={titleRef}>
                        <AnimatedText
                            text={subtitleText}
                            offsets={subtitleLetterOffsets}
                            animated={titleAnimated}
                        />
                    </div>
                </div>
                <div className="space-y-16">
                    {processSteps.map((step, index) => (
                        <div
                            key={index}
                            data-index={index}
                            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center observe-fade-directional opacity-0`}
                        >
                            <div className={`${index % 2 === 1 ? 'lg:order-2' : 'lg:text-right'}`}>
                                <div className="text-6xl font-bold mb-4">
                                    <span className="text-white font-bold"
                                    >
                                        {step.number}
                                    </span>
                                </div>
                                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                    {step.title}
                                </h3>
                                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                                    {step.description}
                                </p>
                            </div>
                            <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} flex items-center justify-center`}>
                                <div className="w-full bg-neutral-900/50 p-4 sm:p-6 lg:p-10 grid-accent">
                                    {stepVisualizations[step.number]}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValuePropositionSection;
